import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const AuthContext = createContext(null)
const AUTH_SESSION_HINT_KEY = 'bizerteconnect:has-auth-session'
const PRIVATE_PATH_PREFIXES = [
  '/merchant',
  '/association',
  '/dashboard',
  '/commerce-dashboard',
  '/prestataire-dashboard',
  '/2fa',
]

function readAuthSessionHint() {
  try {
    return window.localStorage.getItem(AUTH_SESSION_HINT_KEY) === '1'
  } catch {
    return false
  }
}

function writeAuthSessionHint(value) {
  try {
    if (value) window.localStorage.setItem(AUTH_SESSION_HINT_KEY, '1')
    else window.localStorage.removeItem(AUTH_SESSION_HINT_KEY)
  } catch {
    // localStorage can be unavailable in private or restricted browsing modes.
  }
}

function isPrivatePath(pathname = '') {
  return PRIVATE_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

function shouldAttemptInitialRefresh() {
  if (typeof window === 'undefined') return false
  return readAuthSessionHint() || isPrivatePath(window.location.pathname)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    const authApi = await import('./api')
    try {
      const data = await authApi.me()
      setUser(data)
      writeAuthSessionHint(true)
    } catch {
      setUser(null)
      writeAuthSessionHint(false)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (shouldAttemptInitialRefresh()) {
      refresh()
    } else {
      setLoading(false)
    }
  }, [refresh])

  const signIn = useCallback(async ({ email, password }) => {
    const authApi = await import('./api')
    const data = await authApi.login({ email, password })
    // If 2FA is required, return raw response without setting user
    if (data?.requires_2fa) {
      return data
    }
    writeAuthSessionHint(true)
    setUser(data)
    await refresh()
    return data
  }, [refresh])

  const googleSignIn = useCallback(async ({ idToken }) => {
    const authApi = await import('./api')
    const data = await authApi.googleLogin({ idToken })
    const nextUser = data?.user || data
    writeAuthSessionHint(true)
    setUser(nextUser)
    return nextUser
  }, [])

  const confirmTotp = useCallback(async ({ tempToken, code }) => {
    const authApi = await import('./api')
    const data = await authApi.totp2faConfirm({ tempToken, code })
    writeAuthSessionHint(true)
    setUser(data)
    await refresh()
    return data
  }, [refresh])

  const signOut = useCallback(async () => {
    const authApi = await import('./api')
    try {
      await authApi.logout()
    } finally {
      writeAuthSessionHint(false)
      setUser(null)
    }
  }, [])

  const value = useMemo(
    () => ({
      user,
      loading,
      refresh,
      signIn,
      googleSignIn,
      confirmTotp,
      signOut,
    }),
    [user, loading, refresh, signIn, googleSignIn, confirmTotp, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
